<template>
  <div class="app-money-input">
    <input
      :id="id"
      ref="input"
      type="text"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :autocomplete="autocomplete"
      :class="inputClass"
      @focus="onFocus"
      @blur="onBlur"
    />
  </div>
</template>

<script>
import AutoNumeric from 'autonumeric';

export default {
  name: 'AppMoneyInput',
  props: {
    modelValue: {
      type: [String, Number, null],
      default: null,
    },
    id: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '0.00',
    },
    inputClass: {
      type: [String, Array, Object],
      default: 'form-control',
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    locale: {
      type: String,
      default: 'es-MX',
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    currencySymbol: {
      type: String,
      default: '',
    },
    showCurrencySymbol: {
      type: Boolean,
      default: true,
    },
    decimalPlaces: {
      type: Number,
      default: 2,
    },
    allowNegative: {
      type: Boolean,
      default: false,
    },
    minimumValue: {
      type: [String, Number],
      default: '0',
    },
    maximumValue: {
      type: [String, Number, null],
      default: null,
    },
    outputMode: {
      type: String,
      default: 'string',
      validator(value) {
        return ['string', 'number'].includes(String(value || '').toLowerCase());
      },
    },
    emptyValue: {
      type: [String, Number, null],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change', 'focus', 'blur'],
  data() {
    return {
      autoNumeric: null,
      syncingFromOutside: false,
    };
  },
  watch: {
    modelValue(value) {
      this.syncFromModel(value);
    },
    locale() {
      this.reconfigure();
    },
    currency() {
      this.reconfigure();
    },
    currencySymbol() {
      this.reconfigure();
    },
    showCurrencySymbol() {
      this.reconfigure();
    },
    decimalPlaces() {
      this.reconfigure();
    },
    allowNegative() {
      this.reconfigure();
    },
    minimumValue() {
      this.reconfigure();
    },
    maximumValue() {
      this.reconfigure();
    },
  },
  mounted() {
    this.createAutoNumeric();
  },
  beforeUnmount() {
    this.destroyAutoNumeric();
  },
  methods: {
    createAutoNumeric() {
      const input = this.$refs.input;
      if (!input) {
        return;
      }

      try {
        this.autoNumeric = new AutoNumeric(input, null, this.buildAutoNumericOptions());
      } catch (error) {
        // Prevent hard crashes in UI if runtime options are malformed.
        this.autoNumeric = new AutoNumeric(input, null, {
          currencySymbol: '$ ',
          currencySymbolPlacement: 'p',
          decimalCharacter: '.',
          digitGroupSeparator: ',',
          decimalPlaces: 2,
          decimalPlacesShownOnBlur: 2,
          decimalPlacesShownOnFocus: 2,
          minimumValue: '0',
          emptyInputBehavior: 'null',
          modifyValueOnWheel: false,
          watchExternalChanges: false,
        });
      }

      input.addEventListener('autoNumeric:rawValueModified', this.onRawValueModified);
      this.syncFromModel(this.modelValue);
    },
    destroyAutoNumeric() {
      const input = this.$refs.input;
      if (input) {
        input.removeEventListener('autoNumeric:rawValueModified', this.onRawValueModified);
      }

      if (this.autoNumeric) {
        this.autoNumeric.remove();
        this.autoNumeric = null;
      }
    },
    reconfigure() {
      if (!this.autoNumeric) {
        return;
      }

      this.autoNumeric.update(this.buildAutoNumericOptions());
      this.syncFromModel(this.modelValue);
    },
    buildAutoNumericOptions() {
      const currencySymbol = this.showCurrencySymbol ? `${this.resolveCurrencySymbol()} ` : '';
      const decimalPlaces = Number.isFinite(this.decimalPlaces) && this.decimalPlaces >= 0
        ? this.decimalPlaces
        : 2;

      const minimumValue = this.allowNegative
        ? this.normalizeNumericInput(this.minimumValue, '-999999999999.9999')
        : this.normalizeNumericInput(this.minimumValue, '0');

      const options = {
        currencySymbol,
        currencySymbolPlacement: 'p',
        decimalCharacter: '.',
        digitGroupSeparator: ',',
        decimalPlaces,
        decimalPlacesShownOnBlur: decimalPlaces,
        decimalPlacesShownOnFocus: decimalPlaces,
        minimumValue,
        emptyInputBehavior: 'null',
        modifyValueOnWheel: false,
        watchExternalChanges: false,
      };

      const hasMaximumValue = this.maximumValue !== null
        && this.maximumValue !== undefined
        && String(this.maximumValue).trim() !== '';

      if (hasMaximumValue) {
        options.maximumValue = this.normalizeNumericInput(this.maximumValue);
      }

      return options;
    },
    resolveCurrencySymbol() {
      const customSymbol = String(this.currencySymbol || '').trim();
      if (customSymbol !== '') {
        return customSymbol;
      }

      try {
        const parts = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).formatToParts(1);

        const symbolPart = parts.find((part) => part.type === 'currency');
        if (symbolPart?.value) {
          return symbolPart.value;
        }
      } catch (error) {
        return '$';
      }

      return '$';
    },
    normalizeNumericInput(value, fallback = '') {
      const normalized = String(value ?? '').trim();
      return normalized === '' ? fallback : normalized;
    },
    normalizeModelValue(value) {
      if (value === null || value === undefined) {
        return '';
      }

      if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
          return '';
        }

        return String(value);
      }

      const normalized = String(value).trim();
      return normalized;
    },
    syncFromModel(value) {
      if (!this.autoNumeric) {
        return;
      }

      const normalizedValue = this.normalizeModelValue(value);
      const currentRawValue = String(this.autoNumeric.getNumericString() ?? '').trim();

      if (normalizedValue === currentRawValue) {
        return;
      }

      this.syncingFromOutside = true;

      if (normalizedValue === '') {
        this.autoNumeric.clear();
      } else {
        this.autoNumeric.set(normalizedValue);
      }

      this.syncingFromOutside = false;
    },
    castOutputValue(rawValue) {
      const normalizedRaw = String(rawValue ?? '').trim();
      if (normalizedRaw === '') {
        return this.emptyValue;
      }

      if (this.outputMode === 'number') {
        const parsed = Number(normalizedRaw);
        return Number.isFinite(parsed) ? parsed : this.emptyValue;
      }

      return normalizedRaw;
    },
    emitModelValue(rawValue) {
      const payload = this.castOutputValue(rawValue);
      this.$emit('update:modelValue', payload);
      this.$emit('change', payload);
    },
    onRawValueModified(event) {
      if (this.syncingFromOutside) {
        return;
      }

      const rawValue = event?.detail?.newRawValue;
      this.emitModelValue(rawValue);
    },
    onFocus(event) {
      this.$emit('focus', event);
    },
    onBlur(event) {
      this.$emit('blur', event);
    },
  },
};
</script>

<style scoped>
.app-money-input {
  width: 100%;
}
</style>
